import { DEVICE_ROUTES, notificationPreferences, notificationPreferencesOutput, } from "@repo/contract/notifications";
import { getJson, sendJson, } from "../core/request";
import { notificationKeys } from "./keys";
/**
 * `GET`/`PUT /me/notification-preferences` (`D116`).
 *
 * `GET` never 404s: an account with no row reads the defaults. `PUT` replaces
 * the whole shape — the client GETs, edits and PUTs every field back.
 *
 * ── `reminderWeekdays`: THE PERSON'S LOCAL DAYS, `0` SUNDAY ────────────────
 * `null` (or absent) is every day, and absent on a PUT is `null` too — the
 * PUT replaces the whole shape, so a screen that does not show the days must
 * still send back the ones it read. `[]` is refused before sending (the way
 * to stop the reminder is `reminderEnabled: false`). The server answers the
 * days ascending, whatever order they were sent in.
 *
 * ── `utcOffsetMinutes` IS THE CALLER'S TO COMPUTE ──────────────────────────
 * It is the DEVICE's offset at the moment of saving,
 * `new Date().getTimezoneOffset() * -1` (east of UTC positive). This module
 * does not compute it: it has no clock to read that a test could pin, and the
 * screen that converts the picked local hour into `reminderUtcMinute` must use
 * the SAME offset it sends, or the reminder fires at the wrong hour.
 */
/**
 * The input failed the contract's own schema, so nothing was sent.
 *
 * Validation is the form's job (plan D09); this is the backstop that keeps a
 * body the server would answer with a `400` from leaving the device — above
 * all "reminder on" with no hour, which the contract's refinement refuses.
 * `fields` are the offending paths (`["reminderUtcMinute"]`), for a screen
 * that wants to point at one.
 */
export class InvalidNotificationPreferencesError extends Error {
    fields;
    constructor(fields) {
        super(`invalid notification preferences: ${fields.join(", ")}`);
        this.name = "InvalidNotificationPreferencesError";
        this.fields = fields;
    }
}
export function fetchNotificationPreferences(api, init) {
    return getJson(api, DEVICE_ROUTES.preferences, notificationPreferencesOutput, init);
}
/**
 * `PUT` — `safeParse`s the input against `notificationPreferences` first and
 * throws {@link InvalidNotificationPreferencesError} instead of sending. The
 * PARSED value is what goes out, so the wire carries exactly the contract's
 * shape.
 */
export function putNotificationPreferences(api, input) {
    const parsed = notificationPreferences.safeParse(input);
    if (!parsed.success) {
        const fields = parsed.error.issues.map((issue) => issue.path.length === 0 ? "(root)" : issue.path.join("."));
        return Promise.reject(new InvalidNotificationPreferencesError([...new Set(fields)]));
    }
    return sendJson(api, "PUT", DEVICE_ROUTES.preferences, parsed.data, notificationPreferencesOutput);
}
export function notificationPreferencesOptions(api) {
    return {
        queryFn: ({ signal }) => fetchNotificationPreferences(api, { signal }),
        queryKey: notificationKeys.preferences,
    };
}
export function notificationPreferencesMutation(api) {
    return {
        invalidates: [notificationKeys.preferences],
        mutationFn: (input) => putNotificationPreferences(api, input),
        mutationKey: ["notifications", "preferences", "put"],
    };
}
//# sourceMappingURL=preferences.js.map